import { FC, useMemo } from "react";
import { faAdd, faRefresh, faShop } from "@fortawesome/free-solid-svg-icons"
import { Button, IconButton } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { useNavigate } from "react-router-dom";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { DeploymentStatusLabel } from "@hostingbv/lib/src/components/DeploymentStatusLabel";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { EntityNotesButton } from "../components/EntityNotesButton";
import { FaviconPreview } from "@hostingbv/lib/src/components/FaviconPreview";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { useServers, useSites } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";
import { usePersistentState } from "@hostingbv/lib/src/util/usePersistentState";
import { FilterableDataTable, navigateRow } from "@ventumsoftware/react-toolkit";

export const Sites: FC = () => {
  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)

  return (
    <ContentContainer size={"lg"}>
      <PageIconHeader icon={faShop}>
        <Breadcrumbs crumbs={[]} currentPage={"Sites"} />
        <PageHeader>Sites</PageHeader>
      </PageIconHeader>
      {can('create', 'sites') && <AddSiteForm />}
      <SiteOverview />
    </ContentContainer>
  );
}

const AddSiteForm: FC = () => {
  const navigate = useNavigate()
  return <Callout>
    <SectionHeader>Deploy a new site</SectionHeader>
    <Paragraph>
      Sites can be deployed on your servers.
    </Paragraph>
    <Button size={"sm"} type={"primary"} text={"Deploy new site"} icon={faAdd} onClick={() => navigate('/sites/deploy')} />
  </Callout>
}

const formatPhpVersion = (handler: string | null): string => {
  if (!handler) return "n/a"
  const match = handler.match(/(\d+(?:\.\d+)?)/)
  if (!match) return handler
  const version = match[1]
  return version.includes(".") ? version : `${version[0]}.${version.slice(1)}`
}

const SiteOverview: FC = () => {
  const navigate = useNavigate()
  const [siteType, setSiteType] = usePersistentState<string>("sites.filter", "All")

  const { data: sites, isLoading: loadingSites, fetch: fetchSites } = useSites()
  const { data: servers, isLoading: loadingServers, fetch: fetchServers } = useServers()

  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  const reload = () => {
    fetchSites()
    if (can('view', 'servers')) fetchServers()
  }

  const serverLookupTable = useMemo(
    () => new Map((servers ?? []).map((s) => [s.id, s])),
    [servers]
  )

  const mappedSites = useMemo(() => {
    const typeMap: Record<string, string> = {
      "Shared Hosting": "shared_hosting_subscription",
      "Windhosting Site": "windhosting_site",
    };

    const targetType = typeMap[siteType];
    return (sites ?? [])
      .filter((s) => !targetType || s.siteType === targetType)
      .map((site) => {
        const server = serverLookupTable.get(site.serverId) ?? null
        return {
          ...site,
          server,
          serverName: server?.serverType === 'windhosting_server' ? server.displayName.substring(0, server.displayName.lastIndexOf(' ')) : server?.displayName.split('.').slice(0, -2).join('.') ?? null,
          phpHandler: formatPhpVersion(site.phpHandler)
        }
      })
  }, [sites, siteType, serverLookupTable])

  const { getRealtimeStatus } = useRealtimeDeployments()!

  return <div className={"my-8"}>
    <SectionHeader>Your sites</SectionHeader>
    <TabLayout tabNames={["All", "Shared Hosting", "Windhosting Site"]} activeTab={siteType} setActiveTab={setSiteType} />
    <FilterableDataTable
      keyProperty={'id'}
      data={mappedSites}
      searchableProperties={['displayName', (site) => site.customer?.displayName, 'notes', 'phpHandler', 'serverName']}
      actions={<IconButton icon={faRefresh} onClick={reload} type={"secondary"} size={"sm"} />}
      loading={loadingSites || loadingServers}
      columns={[
          {
            header: 'Name',
            property: 'displayName',
            transform: (name, row) => <div className={"flex items-center space-x-2"}>
              <FaviconPreview url={name} />
              <span>{name}</span>
              <EntityNotesButton allowNew={false} entity={row} />
            </div>,
            sort: true,
          },
          {
            header: 'Customer',
            property: 'customer',
            transform: (customer) => customer?.displayName ?? 'n/a',
            sort: true,
          },
          {
            header: 'Type & Server',
            property: 'server',
            transform: (server, row) => {
              if (can('view', 'servers')) {
                return <div>
                  <div className={"text-xs text-slate-500 -mb-1"}>{{ 'shared_hosting_subscription': "Shared Hosting", 'windhosting_site': "Windhosting", 'cluster_hosting': "Cluster Hosting" }[row.siteType]}</div>
                  <div className={"text-sm"}>{server?.serverType === 'windhosting_server' ? server.displayName.substring(0, server.displayName.lastIndexOf(' ')) : server?.displayName.split('.').slice(0, -2).join('.') ?? 'n/a'}</div>
                </div>
              }
              return <div>{{ 'shared_hosting_subscription': "Shared Hosting", 'windhosting_site': "Windhosting", 'cluster_hosting': "Cluster Hosting" }[row.siteType]}</div>
            },
          },
          {
            header: 'Deployment status',
            property: 'deploymentStatus',
            transform: (status, row) => <DeploymentStatusLabel status={getRealtimeStatus(row.id, status)} />
          },
          {
            header: "PHP",
            property: "phpHandler",
            sort: true,
          },
          {
            header: 'Status',
            property: 'removeAt',
            transform: (remove) =>
              <span className={`flex justify-center uppercase font-medium text-[10px] text-white w-28 rounded-sm ${remove ? "bg-red-600" : 'bg-green-600'}`}>{remove ? 'Pending removal' : 'Active'}</span>
          }
        ]}
      onClickRow={(row, e) => navigateRow(navigate, `/sites/${row.id}`, e)}
    />
  </div>
}
